import { Portal, Snackbar } from "@mui/material";
import { useRef, useState } from "react";
import { useAppSelector } from "hooks";
import { RootState } from "redux/store";
import { Button } from "./styles";

interface DebugCopyIdButtonProps {
	id: string;
	label?: string;
	side?: "left" | "right" | "center";
	vertical?: "top" | "bottom";
	style?: React.CSSProperties;
}

const DebugCopyIdButton: React.FC<DebugCopyIdButtonProps> = ({
	id,
	label = "ID",
	side = "right",
	vertical = "bottom",
	style
}) => {
	const showDebugLabels = useAppSelector(
		(state: RootState) => state.debug.showDebugLabels
	);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const buttonRef = useRef<HTMLDivElement>(null);
	const dragRef = useRef({
		isDragging: false,
		startX: 0,
		scrollLeft: 0,
		moved: false
	});

	if (!showDebugLabels) {
		return null;
	}

	const handleClick = async () => {
		if (dragRef.current.moved) {
			return;
		}

		if (expanded) {
			setExpanded(false);
			return;
		}

		try {
			await navigator.clipboard.writeText(id);
			setSnackbarOpen(true);
		} catch (err) {
			console.error("Failed to copy ID:", err);
		}

		setExpanded(true);
	};

	const startDrag = (clientX: number) => {
		dragRef.current.moved = false;
		if (
			!expanded ||
			!buttonRef.current ||
			buttonRef.current.scrollWidth <= buttonRef.current.clientWidth
		) {
			return;
		}
		dragRef.current = {
			isDragging: true,
			startX: clientX,
			scrollLeft: buttonRef.current.scrollLeft,
			moved: false
		};
	};

	const moveDrag = (clientX: number) => {
		if (!dragRef.current.isDragging || !buttonRef.current) {
			return;
		}
		const delta = clientX - dragRef.current.startX;
		if (
			Math.abs(delta) > 3 &&
			buttonRef.current.scrollWidth > buttonRef.current.clientWidth
		) {
			dragRef.current.moved = true;
			buttonRef.current.scrollLeft = dragRef.current.scrollLeft - delta;
		}
	};

	const endDrag = () => {
		dragRef.current.isDragging = false;
	};

	const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
		startDrag(e.clientX);
		if (dragRef.current.isDragging) {
			e.currentTarget.setPointerCapture(e.pointerId);
		}
	};

	const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
		moveDrag(e.clientX);
	};

	const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
		if (e.currentTarget.hasPointerCapture(e.pointerId)) {
			e.currentTarget.releasePointerCapture(e.pointerId);
		}
		endDrag();
	};

	const handlePointerCancel = () => {
		endDrag();
	};

	return (
		<>
			<Button
				ref={buttonRef as React.RefObject<HTMLDivElement>}
				title={id}
				role="button"
				tabIndex={0}
				expanded={expanded}
				side={side}
				vertical={vertical}
				onClick={handleClick}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				onPointerCancel={handlePointerCancel}
				style={{
					cursor: expanded ? "grab" : "pointer",
					...style
				}}
			>
				{expanded ? id : label}
			</Button>
			<Portal>
				<Snackbar
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					open={snackbarOpen}
					autoHideDuration={2000}
					onClose={() => setSnackbarOpen(false)}
					message={`${label} copied to clipboard`}
				/>
			</Portal>
		</>
	);
};

export default DebugCopyIdButton;
